<template>
  <div class="ai-chatbot">
    <!-- 좌측 대화 목록 패널 -->
    <div class="sidebar">
      <div class="sidebar-header">
        <button class="new-chat-btn" @click="createNewConversation">
          <i class="ri-add-line"></i> 새 대화
        </button>
      </div>
      <div class="conversation-items">
        <div v-if="conversations.length === 0" class="no-conversations">
          저장된 대화가 없습니다.
        </div>
        <div
          v-for="conversation in conversations"
          :key="conversation._id"
          class="conversation-item"
          :class="{ 'active': conversationId === conversation._id }"
          @click="loadConversation(conversation._id)"
        >
          <div class="conversation-title">{{ conversation.title }}</div>
          <div class="conversation-date">{{ formatDate(conversation.updatedAt) }}</div>
          <button class="delete-conversation-btn" @click.stop="deleteConversation(conversation._id)">
            <i class="ri-delete-bin-line"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- 우측 대화 패널 -->
    <div class="chat-panel">
      <div class="chat-header">
        <div class="chat-title">
          <img src="@/assets/images/ai-assistant.png" alt="AI Assistant" class="ai-avatar" v-if="false">
          <h3>AI 어시스턴트</h3>
        </div>
        <div class="chat-actions">
          <button class="clear-btn" @click="clearChat">
            <i class="ri-delete-bin-line"></i> 대화 초기화
          </button>
        </div>
      </div>

      <div class="chat-messages" ref="messagesContainer">
        <div v-if="messages.length === 0" class="empty-chat">
          <div class="welcome-message">
            <h4>AI 어시스턴트에게 질문해보세요</h4>
            <p>제품 분석, 마케팅 전략, 시장 동향 등에 대해 물어보세요.</p>
          </div>
          <div class="suggested-prompts">
            <h5>추천 질문</h5>
            <div class="prompt-buttons">
              <button
                v-for="(prompt, index) in suggestedPrompts"
                :key="index"
                @click="sendSuggestedPrompt(prompt)"
                class="prompt-btn"
              >
                {{ prompt }}
              </button>
            </div>
          </div>
        </div>

        <template v-else>
          <chat-message
            v-for="(message, index) in messages"
            :key="index"
            :message="message"
            :is-last="index === messages.length - 1"
          />
        </template>

        <chat-typing-indicator v-if="isTyping" />
      </div>

      <chat-input
        @send-message="sendMessage"
        :is-loading="isTyping"
      />
    </div>
  </div>
</template>

<script>
import ChatMessage from './ChatMessage.vue';
import ChatInput from './AIChatInput.vue';
import ChatTypingIndicator from './ChatTypingIndicator.vue';
import { marked } from 'marked';
import kbService from '@/services/kbService';
import confirmInPageMixin from '@/mixins/confirmInPageMixin';

// marked 설정 - 링크에 target="_blank" 추가
// 원본 renderer 저장
const renderer = new marked.Renderer();
const originalLinkRenderer = renderer.link;

// 링크 렌더러 재정의
renderer.link = function(href, title, text) {
  const html = originalLinkRenderer.call(this, href, title, text);
  // target="_blank" 추가
  return html.replace(/^<a /, '<a target="_blank" rel="noopener noreferrer" ');
};

// marked 옵션 설정
marked.setOptions({ renderer });

export default {
  mixins: [confirmInPageMixin],
  name: 'AIChatBot',
  components: {
    ChatMessage,
    ChatInput,
    ChatTypingIndicator
  },
  data() {
    return {
      messages: [],
      isTyping: false,
      conversationId: null,
      conversations: [],
      suggestedPrompts: [
        '이번 달 판매 실적을 분석해줘',
        '경쟁사 제품과 비교 분석해줘',
        '마케팅 전략 추천해줘',
        '시장 트렌드 분석해줘'
      ]
    };
  },
  mounted() {
    // 대화 목록 불러오기 (오류가 발생해도 계속 진행)
    this.fetchConversations().catch(error => {
      console.log('대화 목록을 불러오는 중 오류가 발생했습니다:', error);
      this.conversations = [];
    });

    // 초기 상태에서는 새 대화를 자동으로 생성하지 않고, 사용자가 메시지를 보낼 때 생성함
    // 빈 메시지 배열로 시작
    this.messages = [];

    // 환영 메시지 추가
    this.addBotMessage('안녕하세요! AI 어시스턴트입니다. 무엇을 도와드릴까요?');
  },
  methods: {
    async sendMessage(data) {
      // 텍스트 메시지나 이미지 중 하나는 있어야 함
      if ((!data.text || !data.text.trim()) && !data.images?.length && !data.image) return;

      // 대화 ID가 없는 경우 새 대화 생성
      if (!this.conversationId) {
        await this.createNewConversation();
      }

      // 사용자 메시지 추가
      this.addUserMessage(data);

      // AI 응답 요청
      const messageText = data.text || '이미지를 분석해 주세요.';
      this.simulateBotResponse(messageText, data.images);
    },

    addUserMessage(data) {
      // 여러 이미지가 있는 경우 처리 (AIChatInput에서 전송)
      if (data.images && data.images.length > 0) {
        console.log('[AI-ASSISTANT] 여러 이미지 처리:', data.images.length, '개');

        // 이미지들을 Data URL로 변환
        const imagePromises = data.images.map(file => {
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = e => resolve(e.target.result);
            reader.readAsDataURL(file);
          });
        });

        Promise.all(imagePromises).then(imageDataUrls => {
          this.messages.push({
            type: 'user',
            content: data.text || '',
            images: imageDataUrls, // 여러 이미지 저장
            imageFiles: data.images, // 원본 파일들도 저장 (API 전송용)
            timestamp: new Date()
          });
          this.scrollToBottom();
        });
      }
      // 단일 이미지가 있는 경우 처리 (기존 호환성)
      else if (data.image) {
        console.log('[AI-ASSISTANT] 단일 이미지 처리');
        const reader = new FileReader();
        reader.onload = e => {
          this.messages.push({
            type: 'user',
            content: data.text || '',
            image: e.target.result,
            timestamp: new Date()
          });
          this.scrollToBottom();
        };
        reader.readAsDataURL(data.image);
      }
      // 텍스트만 있는 경우
      else {
        this.messages.push({
          type: 'user',
          content: data.text,
          timestamp: new Date()
        });
        this.scrollToBottom();
      }
    },

    addBotMessage(text, image = null) {
      // 텍스트 유효성 검사
      if (!text || typeof text !== 'string') {
        console.error('[AI-ASSISTANT] addBotMessage: 유효하지 않은 텍스트:', text);
        text = '죄송합니다. 응답을 처리하는 중에 오류가 발생했습니다.';
      }

      // 마크다운을 HTML로 변환
      const parsedContent = marked.parse(text);

      this.messages.push({
        type: 'bot',
        content: parsedContent,
        rawContent: text, // 원본 마크다운 텍스트 저장
        image: image,
        timestamp: new Date(),
        isMarkdown: true
      });
      this.scrollToBottom();
    },

    // 대화 목록 불러오기
    async fetchConversations() {
      try {
        const userId = this.$store.state.auth?.user?.id || 'anonymous';
        const response = await fetch(`/api/ai-conversations?userId=${userId}`);

        // 응답이 JSON이 아닌 경우 처리
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.log('대화 목록을 가져오는 중 오류가 발생했습니다. JSON이 아닌 응답을 받았습니다.');
          // 오류가 발생해도 빈 배열로 초기화하여 UI가 깨지지 않도록 함
          this.conversations = [];
          return;
        }

        const data = await response.json();

        if (data.success) {
          this.conversations = data.conversations;
        } else {
          console.log('대화 목록을 가져오는 중 오류가 발생했습니다:', data.message);
          // 오류 발생 시 빈 배열로 초기화
          this.conversations = [];
        }
      } catch (error) {
        // 오류 메시지를 console.error 대신 console.log로 변경하여 덜 눈에 띄게 함
        console.log('대화 목록을 가져오는 중 오류가 발생했습니다:', error);
        // 오류 발생 시 빈 배열로 초기화
        this.conversations = [];
      }
    },

    // 새 대화 생성
    async createNewConversation() {
      try {
        const userId = this.$store.state.auth?.user?.id || 'anonymous';
        const response = await fetch('/api/ai-conversations', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            title: '새 대화',
            userId: userId
          })
        });

        // 응답이 JSON이 아닌 경우 처리
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.log('새 대화를 생성하는 중 오류가 발생했습니다. JSON이 아닌 응답을 받았습니다.');
          // 오류 발생 시 로컬에서만 새 대화 생성
          this.conversationId = null;
          this.messages = [];
          this.addBotMessage('안녕하세요! AI 어시스턴트입니다. 무엇을 도와드릴까요?');
          return;
        }

        const data = await response.json();

        if (data.success) {
          this.conversationId = data.conversationId;
          this.messages = [];
          this.addBotMessage('안녕하세요! AI 어시스턴트입니다. 무엇을 도와드릴까요?');
          this.fetchConversations();
        } else {
          console.log('새 대화를 생성하는 중 오류가 발생했습니다:', data.message);
          // 오류 발생 시 로컬에서만 새 대화 생성
          this.conversationId = null;
          this.messages = [];
          this.addBotMessage('안녕하세요! AI 어시스턴트입니다. 무엇을 도와드릴까요?');
        }
      } catch (error) {
        console.log('새 대화를 생성하는 중 오류가 발생했습니다:', error);
        // 오류 발생 시 로컬에서만 새 대화 생성
        this.conversationId = null;
        this.messages = [];
        this.addBotMessage('안녕하세요! AI 어시스턴트입니다. 무엇을 도와드릴까요?');
      }
    },

    // 대화 불러오기
    async loadConversation(conversationId) {
      try {
        const response = await fetch(`/api/ai-conversations/${conversationId}`);

        // 응답이 JSON이 아닌 경우 처리
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.log('대화를 불러오는 중 오류가 발생했습니다. JSON이 아닌 응답을 받았습니다.');
          // 오류 발생 시 새 대화 생성
          this.createNewConversation();
          return;
        }

        const data = await response.json();

        if (data.success) {
          this.conversationId = conversationId;
          this.messages = data.conversation.messages;
        } else {
          console.log('대화를 불러오는 중 오류가 발생했습니다:', data.message);
          // 오류 발생 시 새 대화 생성
          this.createNewConversation();
        }
      } catch (error) {
        console.log('대화를 불러오는 중 오류가 발생했습니다:', error);
        // 오류 발생 시 새 대화 생성
        this.createNewConversation();
      }
    },

    // 대화 삭제
    async deleteConversation(conversationId) {
      if (!(await this.confirmInPage('정말 이 대화를 삭제하시겠습니까?'))) return;

      try {
        const response = await fetch(`/api/ai-conversations/${conversationId}`, {
          method: 'DELETE'
        });

        // 응답이 JSON이 아닌 경우 처리
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.log('대화를 삭제하는 중 오류가 발생했습니다. JSON이 아닌 응답을 받았습니다.');
          // 오류가 발생해도 UI 업데이트를 위해 대화 목록 새로고침
          this.fetchConversations();
          return;
        }

        const data = await response.json();

        if (data.success) {
          // 현재 대화가 삭제된 경우 새 대화 생성
          if (this.conversationId === conversationId) {
            this.createNewConversation();
          }

          // 대화 목록 업데이트
          this.fetchConversations();
        } else {
          console.log('대화를 삭제하는 중 오류가 발생했습니다:', data.message);
          // 오류가 발생해도 UI 업데이트를 위해 대화 목록 새로고침
          this.fetchConversations();
        }
      } catch (error) {
        console.log('대화를 삭제하는 중 오류가 발생했습니다:', error);
        // 오류가 발생해도 UI 업데이트를 위해 대화 목록 새로고침
        this.fetchConversations();
      }
    },



    // 날짜 포맷팅
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    async simulateBotResponse(userMessage, userImages = null) {
      this.isTyping = true;

      try {
        console.log('[AI-ASSISTANT] sendMessage 시작');
        console.log('[AI-ASSISTANT] userMessage:', userMessage);
        console.log('[AI-ASSISTANT] userImages:', userImages);

        // 이미지가 있는 경우 일반 AI 응답 사용
        const lastMessage = this.messages[this.messages.length - 1];
        console.log('[AI-ASSISTANT] lastMessage:', lastMessage);
        console.log('[AI-ASSISTANT] 이미지 체크 - lastMessage.image:', !!lastMessage.image);
        console.log('[AI-ASSISTANT] 이미지 체크 - lastMessage.images:', !!lastMessage.images);
        console.log('[AI-ASSISTANT] 이미지 체크 - userImages:', !!userImages);

        if (lastMessage.image || lastMessage.images || (userImages && userImages.length > 0)) {
          console.log('[AI-ASSISTANT] 이미지가 있어서 일반 AI 응답 사용');
          await this.getRegularAIResponse(userMessage, userImages);
          return;
        }

        console.log('[AI-ASSISTANT] 이미지가 없어서 RAG 기능 사용 예정');

        // 이미지가 없는 경우 RAG 기능 사용
        try {
          // RAG 기능 호출
          console.log('[AI-ASSISTANT] RAG 기능 호출 시작');
          console.log('[AI-ASSISTANT] 사용자 메시지:', userMessage);

          // 키 값은 안 읽는다 — 서버가 세션 회사 Settings 키를 쓴다
          if (!this.$store.state.settings[0]?.hasGeminiKey) {
            console.error('[AI-ASSISTANT] API 키가 없습니다. 일반 AI 응답으로 대체합니다.');
            await this.getRegularAIResponse(userMessage);
            return;
          }

          console.log('[AI-ASSISTANT] kbService.chatWithRAG 호출 중...');
          const ragResponse = await kbService.chatWithRAG(userMessage, this.conversationId);

          console.log('[AI-ASSISTANT] RAG 응답 전체:', ragResponse);
          console.log('[AI-ASSISTANT] RAG 응답 텍스트:', ragResponse.response);
          console.log('[AI-ASSISTANT] RAG isRAG:', ragResponse.isRAG);
          console.log('[AI-ASSISTANT] RAG 출처:', ragResponse.sources);

          // 응답 텍스트 유효성 검사
          if (!ragResponse.response) {
            console.error('[AI-ASSISTANT] RAG 응답 텍스트가 없습니다. 일반 AI 응답으로 대체합니다.');
            await this.getRegularAIResponse(userMessage);
            return;
          }

          // RAG 응답 처리
          if (ragResponse.isRAG && ragResponse.sources && ragResponse.sources.length > 0) {
            // 소스 정보가 있는 경우 (RAG 응답)
            console.log('[AI-ASSISTANT] RAG 응답 성공 (소스 있음)');
            this.addBotMessage(ragResponse.response);
          } else {
            // 소스 정보가 없는 경우 (일반 응답)
            console.log('[AI-ASSISTANT] RAG 응답 성공 (소스 없음)');
            this.addBotMessage(ragResponse.response);
          }
        } catch (ragError) {
          console.error('[AI-ASSISTANT] RAG 오류, 일반 AI 응답으로 대체:', ragError);
          console.error('[AI-ASSISTANT] RAG 오류 상세:', ragError.stack);
          // RAG 오류 시 일반 AI 응답으로 대체
          await this.getRegularAIResponse(userMessage);
        }
      } catch (error) {
        console.error('[AI-ASSISTANT] 응답 오류:', error);
        this.addBotMessage('죄송합니다. 서버와 통신 중에 오류가 발생했습니다. 다시 시도해 주세요.');
      } finally {
        this.isTyping = false;
      }
    },

    // 일반 AI 응답 가져오기 (이미지 처리 또는 RAG 실패 시 사용)
    async getRegularAIResponse(userMessage, userImages = null) {
      try {
        // 이미지 처리 - 여러 이미지 지원
        let imageUrls = [];
        const lastMessage = this.messages[this.messages.length - 1];

        // 1. 함수 매개변수로 전달된 이미지들 (File 객체들)
        if (userImages && userImages.length > 0) {
          console.log('[AI-ASSISTANT] 매개변수 이미지 처리:', userImages.length, '개');
          // File 객체들을 Data URL로 변환
          for (const file of userImages) {
            const dataUrl = await this.fileToDataUrl(file);
            imageUrls.push(dataUrl);
          }
        }
        // 2. 메시지에 저장된 여러 이미지들
        else if (lastMessage.images && lastMessage.images.length > 0) {
          console.log('[AI-ASSISTANT] 메시지 여러 이미지 처리:', lastMessage.images.length, '개');
          imageUrls = [...lastMessage.images];
        }
        // 3. 메시지에 저장된 단일 이미지 (기존 호환성)
        else if (lastMessage.image) {
          console.log('[AI-ASSISTANT] 메시지 단일 이미지 처리');
          imageUrls.push(lastMessage.image);
        }

        console.log('[AI-ASSISTANT] 최종 이미지 URL 개수:', imageUrls.length);

        // API 키 있나(키 값은 서버만 씀)
        if (!(this.$store.state.settings && this.$store.state.settings[0] && this.$store.state.settings[0].hasGeminiKey)) {
          this.addBotMessage('API 키가 설정되지 않았습니다. 설정에서 Gemini API 키를 추가해주세요.');
          return;
        }

        // 대화 컨텍스트 구성 (최근 5개 메시지만 포함)
        const conversationContext = this.messages
          .slice(-5) // 최근 5개 메시지만 가져옴
          .map(msg => {
            // 원본 마크다운 텍스트가 있으면 사용, 없으면 content 사용
            const content = msg.rawContent || msg.content;
            return `${msg.type === 'user' ? '사용자' : 'AI'}: ${content}`;
          })
          .join('\n\n');

        // 현재 메시지와 컨텍스트 결합
        const promptWithContext = conversationContext ?
          `${conversationContext}\n\n사용자: ${userMessage}` :
          userMessage;

        // API 호출
        const response = await fetch('/api/ai-assistant', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            prompt: promptWithContext,
            imageUrls: imageUrls,
            conversationId: this.conversationId // 대화 ID 추가
          })
        });

        const data = await response.json();

        if (data.success) {
          this.addBotMessage(data.response);
        } else {
          // 서버 글(로그인 필요·Gemini 키 없음 안내)을 그대로 보여 준다
          this.addBotMessage(data.message || '죄송합니다. 응답을 생성하는 중에 오류가 발생했습니다. 다시 시도해 주세요.');
          console.error('AI 응답 오류:', data.message || '알 수 없는 오류');
        }
      } catch (error) {
        console.error('일반 AI 응답 오류:', error);
        this.addBotMessage('죄송합니다. 서버와 통신 중에 오류가 발생했습니다. 다시 시도해 주세요.');
      }
    },

    sendSuggestedPrompt(prompt) {
      this.sendMessage({ text: prompt });
    },

    async clearChat() {
      if (await this.confirmInPage('대화 내용을 모두 지우시겠습니까?')) {
        this.messages = [];
        // 초기 메시지 다시 추가
        this.addBotMessage('안녕하세요! AI 어시스턴트입니다. 무엇을 도와드릴까요?');
      }
    },

    // File 객체를 Data URL로 변환하는 헬퍼 함수
    fileToDataUrl(file) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = e => resolve(e.target.result);
        reader.readAsDataURL(file);
      });
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        container.scrollTop = container.scrollHeight;
      });
    }
  }
};
</script>

<style scoped>
.ai-chatbot {
  display: flex;
  height: 90vh;
  min-height: 600px;
  background-color: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

/* 좌측 사이드바 스타일 */
.sidebar {
  width: 280px;
  background-color: #f9fafb;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sidebar-header {
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.new-chat-btn {
  width: 100%;
  background-color: #4f46e5;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.new-chat-btn:hover {
  background-color: #4338ca;
}

.new-chat-btn i {
  margin-right: 8px;
}

/* 우측 대화 패널 스타일 */
.chat-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.chat-title {
  display: flex;
  align-items: center;
}

.ai-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  margin-right: 12px;
}

.chat-title h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
}

.clear-btn {
  background: none;
  border: none;
  color: #6b7280;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.clear-btn:hover {
  background-color: #f3f4f6;
  color: #4b5563;
}

.clear-btn i {
  margin-right: 8px;
}

.chat-messages {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
  background-color: #ffffff;
  background-image: linear-gradient(rgba(240, 240, 250, 0.1) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(240, 240, 250, 0.1) 1px, transparent 1px);
  background-size: 20px 20px;
}

.empty-chat {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  color: #6b7280;
  padding: 20px;
}

.welcome-message {
  margin-bottom: 32px;
}

.welcome-message h4 {
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #111827;
  line-height: 1.3;
}

.welcome-message p {
  font-size: 16px;
  color: #6b7280;
  line-height: 1.5;
}



.suggested-prompts {
  width: 100%;
  max-width: 600px;
}

.suggested-prompts h5 {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #374151;
}

.prompt-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
}

.prompt-btn {
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 24px;
  padding: 10px 18px;
  font-size: 14px;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.prompt-btn:hover {
  background-color: #f3f4f6;
  border-color: #d1d5db;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}
/* 대화 목록 스타일 */
.action-btn {
  background: none;
  border: none;
  color: #6b7280;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 8px;
  transition: all 0.2s ease;
  margin-right: 8px;
}

.action-btn:hover {
  background-color: #f3f4f6;
  color: #4b5563;
}

.action-btn i {
  margin-right: 8px;
}

.conversation-items {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.no-conversations {
  padding: 16px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
}

.conversation-item {
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
}

.conversation-item:hover {
  background-color: #f3f4f6;
}

.conversation-item.active {
  background-color: #e5e7eb;
}

.conversation-title {
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.conversation-date {
  font-size: 12px;
  color: #6b7280;
}

.delete-conversation-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 16px;
  opacity: 0;
  transition: opacity 0.2s ease;
  padding: 4px;
  border-radius: 4px;
}

.conversation-item:hover .delete-conversation-btn {
  opacity: 1;
}

.delete-conversation-btn:hover {
  background-color: #fee2e2;
  color: #ef4444;
}
</style>
